import { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useUserConfig } from '../hooks/useUserConfig';
import { usePagination } from '../hooks/usePagination';
import { useTableSort } from '../hooks/useTableSort';
import { useFetchPaginatedData } from '../hooks/useFetchPaginatedData';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { serializeParams } from '../utils/params';
import PaginationControls from '../components/PaginationControls';
import ErrorLoading from '../components/ErrorLoading';
import AutoRefreshControls from '../components/AutoRefreshControls';
import { formatDateTime } from '../utils/format';

const allHandicaps = [
  0, 0.25, -0.25, 0.5, -0.5, 0.75, -0.75, 1, -1, 1.25, -1.25, 1.5, -1.5, 1.75, -1.75, 2, -2,
];
const defaultHandicaps = [...allHandicaps];
const dataSource = 'sg_pools';

function SgPoolsData({ token, backendUrl, dateRange, setDateRange, error, setError, handleApplyDates }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const pagination = usePagination('sgPools.pageSize', 50);
  const { sortField, sortDirection, sortableHeader } = useTableSort('start_time', 'asc');
  const [homeHandicaps, setHomeHandicaps] = useUserConfig('sgPools.homeHandicap', defaultHandicaps);
  const [awayHandicaps, setAwayHandicaps] = useUserConfig('sgPools.awayHandicap', defaultHandicaps);

  const params = useMemo(() => ({
    start_date: dateRange.start_date,
    end_date: dateRange.end_date,
    home_handicap: homeHandicaps,
    away_handicap: awayHandicaps,
  }), [dateRange.start_date, dateRange.end_date, homeHandicaps, awayHandicaps]);

  const { data, loading, refetch } = useFetchPaginatedData({
    dataUrl: `${backendUrl}/sg_pools/dates`,
    countUrl: `${backendUrl}/sg_pools/dates/count`,
    token,
    enabled: Boolean(token && dateRange.start_date && dateRange.end_date && !error),
    params,
    page: pagination.currentPage,
    pageSize: pagination.pageSize,
    setTotalCount: pagination.setTotalCount,
    setError,
  });

  const autoRefresh = useAutoRefresh(() => refetch(), { defaultInterval: 60, defaultEnabled: true });

  const handleHandicapChange = useCallback((handicap, isChecked, isHome) => {
    const setHandicaps = isHome ? setHomeHandicaps : setAwayHandicaps;
    setHandicaps((prev) => {
      const next = isChecked
        ? [...prev, handicap].filter((v, i, self) => self.indexOf(v) === i)
        : prev.filter((h) => h !== handicap);
      pagination.resetPage();
      return next;
    });
  }, [setHomeHandicaps, setAwayHandicaps, pagination]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    pagination.resetPage();
  }, [pagination]);

  const handleDateChange = useCallback((e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  }, [setDateRange]);

  const { filteredAndSortedData, pagedData } = useMemo(() => {
    const dataToFilter = Array.isArray(data) ? data : [];
    const normalizedQuery = searchQuery.toLowerCase().trim();

    const filteredData = normalizedQuery
      ? dataToFilter.filter((item) =>
          String(item.date_event_id).toLowerCase().includes(normalizedQuery) ||
          String(item.name).toLowerCase().includes(normalizedQuery)
        )
      : dataToFilter;

    const sorted = [...filteredData].sort((a, b) => {
      if (!sortField) return 0;
      const aValue = a[sortField] ? new Date(a[sortField]).getTime() : 0;
      const bValue = b[sortField] ? new Date(b[sortField]).getTime() : 0;
      if (isNaN(aValue) || isNaN(bValue)) {
        const aStr = String(a[sortField] ?? '').toLowerCase();
        const bStr = String(b[sortField] ?? '').toLowerCase();
        if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return { filteredAndSortedData: sorted, pagedData: sorted };
  }, [data, searchQuery, sortField, sortDirection]);

  const handleApplyClick = useCallback(() => {
    pagination.resetPage();
    setError(null);
    handleApplyDates(dataSource, homeHandicaps, awayHandicaps);
  }, [pagination, setError, handleApplyDates, homeHandicaps, awayHandicaps]);

  const downloadCSV = async () => {
    try {
      const response = await axios.get(`${backendUrl}/sg_pools/dates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          skip: 0,
          limit: -1,
          home_handicap: homeHandicaps,
          away_handicap: awayHandicaps,
        },
        paramsSerializer: serializeParams,
      });
      const allEventData = Array.isArray(response.data.data) ? response.data.data : [];

      const normalizedQuery = searchQuery.toLowerCase().trim();
      const filteredData = normalizedQuery
        ? allEventData.filter((item) =>
            String(item.date_event_id).toLowerCase().includes(normalizedQuery) ||
            String(item.name).toLowerCase().includes(normalizedQuery)
          )
        : allEventData;

      const finalSortedData = [...filteredData].sort((a, b) => {
        if (!sortField) return 0;
        const aValue = a[sortField] ? new Date(a[sortField]).getTime() : 0;
        const bValue = b[sortField] ? new Date(b[sortField]).getTime() : 0;
        if (isNaN(aValue) || isNaN(bValue)) {
          const aStr = String(a[sortField] ?? '').toLowerCase();
          const bStr = String(b[sortField] ?? '').toLowerCase();
          if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
          if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });

      const headers = [
        t('sgPools.eventId'), t('sgPools.name'), t('sgPools.startTime'),
        t('sgPools.firstHomeHandicap'), t('sgPools.firstAwayHandicap'),
        t('sgPools.homeHandicap'), t('sgPools.awayHandicap'),
        t('sgPools.homeOdds'), t('sgPools.awayOdds'),
        t('sgPools.halfScore'), t('sgPools.fullScore'),
        t('common.createdAt'), t('common.updatedAt'),
      ];

      const rows = finalSortedData.map((item) => [
        item.date_event_id || '',
        item.name || '',
        formatDateTime(item.start_time),
        item.first_home_handicap || '',
        item.first_away_handicap || '',
        item.home_handicap || '',
        item.away_handicap || '',
        item.home_price || '',
        item.away_price || '',
        item.half_score || '',
        item.full_score || '',
        formatDateTime(item.created_at),
        formatDateTime(item.updated_at),
      ]);

      const csvContent = [
        '﻿' + headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${dataSource}_data_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('下載 CSV 錯誤:', err);
      setError(t('common.csvDownloadError'));
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center my-4">
        <h2>{t('sgPools.title')}</h2>
        <div className="d-flex align-items-center gap-3">
          <AutoRefreshControls {...autoRefresh} />
          <button className="btn btn-success" onClick={downloadCSV}>
            {t('common.downloadCsv')}
          </button>
        </div>
      </div>

      <div className="mb-3">
        <div className="row g-2 align-items-center">
          <div className="col-auto">
            <label htmlFor="search" className="form-label">{t('common.search')}</label>
            <input
              type="text"
              id="search"
              value={searchQuery}
              onChange={handleSearchChange}
              className="form-control"
              placeholder={t('sgPools.searchPlaceholder')}
            />
          </div>
          <div className="col-auto">
            <label htmlFor="start_date" className="form-label">{t('common.startDate')}</label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={dateRange.start_date}
              onChange={handleDateChange}
              className="form-control"
            />
          </div>
          <div className="col-auto">
            <label htmlFor="end_date" className="form-label">{t('common.endDate')}</label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={dateRange.end_date}
              onChange={handleDateChange}
              className="form-control"
            />
          </div>
          <div className="col-auto align-self-end">
            <button className="btn btn-primary" onClick={handleApplyClick}>
              {t('common.apply')}
            </button>
          </div>
        </div>

        <p className="text-muted small mb-3">
          {t('common.dateRange')}: {dateRange.start_date} {t('common.to')} {dateRange.end_date}
          {pagination.totalCount > 0 && <span> | {t('common.totalCount')}: {pagination.totalCount} | {t('common.filteredCount')}: {filteredAndSortedData.length}</span>}
        </p>

        <div className="mb-3">
          <div className="row g-2 align-items-start">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">{t('sgPools.homeHandicapFilter')}</div>
                <div className="card-body">
                  <div className="d-flex flex-wrap gap-2">
                    {allHandicaps.map((h) => (
                      <div className="form-check form-check-inline" key={`home-${h}`}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`home_hcp_${h}`}
                          value={h}
                          checked={homeHandicaps.includes(h)}
                          onChange={(e) => handleHandicapChange(h, e.target.checked, true)}
                        />
                        <label className="form-check-label" htmlFor={`home_hcp_${h}`}>
                          {h > 0 ? `+${h}` : h}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card">
                <div className="card-header">{t('sgPools.awayHandicapFilter')}</div>
                <div className="card-body">
                  <div className="d-flex flex-wrap gap-2">
                    {allHandicaps.map((h) => (
                      <div className="form-check form-check-inline" key={`away-${h}`}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`away_hcp_${h}`}
                          value={h}
                          checked={awayHandicaps.includes(h)}
                          onChange={(e) => handleHandicapChange(h, e.target.checked, false)}
                        />
                        <label className="form-check-label" htmlFor={`away_hcp_${h}`}>
                          {h > 0 ? `+${h}` : h}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <PaginationControls {...pagination} />
      </div>

      <ErrorLoading error={error} loading={loading} />

      {pagedData && Array.isArray(pagedData) && !loading ? (
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-hover">
            <thead>
              <tr>
                <th>{t('sgPools.eventId')}</th>
                <th>{t('sgPools.name')}</th>
                {sortableHeader(t('sgPools.startTime'), 'start_time')}
                <th>{t('sgPools.firstHomeHandicap')}</th>
                <th>{t('sgPools.firstAwayHandicap')}</th>
                <th>{t('sgPools.homeHandicap')}</th>
                <th>{t('sgPools.awayHandicap')}</th>
                <th>{t('sgPools.homeOdds')}</th>
                <th>{t('sgPools.awayOdds')}</th>
                <th>{t('sgPools.halfScore')}</th>
                <th>{t('sgPools.fullScore')}</th>
                {sortableHeader(t('common.createdAt'), 'created_at')}
                {sortableHeader(t('common.updatedAt'), 'updated_at')}
              </tr>
            </thead>
            <tbody>
              {pagedData.map((item) => (
                <tr key={item.date_event_id}>
                  <td>
                    <Link to={`/events/${item.date_event_id}`} target="_blank">
                      {item.date_event_id}
                    </Link>
                  </td>
                  <td><a href={item.url}>{item.name}</a></td>
                  <td>{formatDateTime(item.start_time)}</td>
                  <td>{item.first_home_handicap}</td>
                  <td>{item.first_away_handicap}</td>
                  <td>{item.home_handicap}</td>
                  <td>{item.away_handicap}</td>
                  <td>{item.home_price}</td>
                  <td>{item.away_price}</td>
                  <td>{item.half_score}</td>
                  <td>{item.full_score}</td>
                  <td>{formatDateTime(item.created_at)}</td>
                  <td>{formatDateTime(item.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && <p className="text-muted">{t('common.noData')}</p>
      )}
    </div>
  );
}

export default SgPoolsData;
